
import { useQuasar, useMeta, QVirtualScrollProps } from 'quasar';
import { ref, onMounted, watch } from 'vue';
import API, { Channel, ChannelFilters } from 'boot/api';
import {
  matClose,
  matSearch,
  matRefresh,
  matDelete,
} from '@quasar/extras/material-icons';
import AddChannelCard from '../components/AddChannelCard.vue';

export default {
  name: 'ChannelsPage',
  components: { AddChannelCard },
  setup() {
    const $q = useQuasar();
    const channelLoadLimit = 100;
    let hasNextPage = true;
    let abortRequest: AbortController | undefined;

    const loading = ref(true);
    const nameFilter = ref('');
    const channels = ref<readonly Channel[]>(Object.freeze([]));
    const addChannelDialog = ref(false);

    useMeta({
      title: 'Channels',
    });

    const onRequest = async (filters?: ChannelFilters, overwrite = false) => {
      try {
        loading.value = true;
        if (abortRequest) {
          // Cancel ongoing request.
          abortRequest.abort();
        }

        const { request, controller } = API.getChannels({
          limit: channelLoadLimit,
          name: nameFilter.value,
          ...filters,
        });

        abortRequest = controller;
        const data = await request;
        hasNextPage = data.length >= (filters?.limit || channelLoadLimit);

        if (overwrite) {
          channels.value = Object.freeze(data);
        } else {
          channels.value = Object.freeze(channels.value.concat(data));
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Ignore abort errors.
          return;
        }

        $q.notify({
          type: 'negative',
          message: String(err),
        });
      } finally {
        loading.value = false;
      }
    };

    const onScroll: QVirtualScrollProps['onVirtualScroll'] = async ({ to }) => {
      const channelCount = channels.value.length;
      const lastIndex = channelCount - 1;

      if (hasNextPage && channelCount > 0 && !loading.value && to === lastIndex) {
        const { id, updatedAt } = channels.value[lastIndex];

        await onRequest({
          limit: channelLoadLimit,
          continueId: id,
          continueTime: updatedAt,
        });
      }
    };

    const onAddChannel = (channel: Channel) => {
      // Close add channel dialog and reset name filter.
      addChannelDialog.value = false;
      nameFilter.value = '';

      // Prepend the added channel.
      channels.value = Object.freeze([channel].concat(channels.value));
    };

    async function onDeleteChannel({ row, rowIndex }: { row: Channel; rowIndex: number; }) {
      try {
        loading.value = true;
        await API.deleteChannel(row.id);

        // Remove deleted channel.
        const channelsCopy = channels.value.slice();
        channelsCopy.splice(rowIndex, 1);
        channels.value = Object.freeze(channelsCopy);

        // Get updated channels from API.
        await onRequest({}, true);
      } catch (err) {
        $q.notify({
          type: 'negative',
          message: String(err),
        });
      } finally {
        loading.value = false;
      }
    }

    const onRefresh = async () => {
      if (!loading.value) {
        await onRequest({}, true);
      }
    };

    onMounted(async () => {
      await onRequest({}, true);
    });

    watch(nameFilter, onRefresh);

    return {
      loading,
      nameFilter,
      channels,
      addChannelDialog,
      onScroll,
      onAddChannel,
      onDeleteChannel,
      onRefresh,
      matClose,
      matSearch,
      matRefresh,
      matDelete,
    };
  },
};
